import "../styles/side-bar.scss";
import { useState } from "react";
import actions from "../actions/actions";
import { AlertStatus, Path, UserRole } from "../utilities/constants";
import { useSelector } from "react-redux";
import { Blackout } from "./modal";
import navigate from "../utilities/navigation";
import { useEffect } from "react";
import Resizer from "./resizer";
import { useLogoutIfAccessDisabled } from "../utilities/hooks";

export default function SideNavigation() {
  let { sidebarExpanded, isMobile } = useSelector((store) => store.page);
  let [expandedItem, setExpandedItem] = useState();
  let loginUser = useSelector((store) => store.user);
  let path = useSelector((store) => store.router.location.pathname);
  const isMemberOrStaff = [1, 2, 3].includes(loginUser?.userType);

  // Forcefully log out user if access is disabled
  useLogoutIfAccessDisabled(loginUser);

  // Redirect to dashboard if user manually enters another URL
  useEffect(() => {
    if (isMemberOrStaff && loginUser?.read_only && path !== Path.Dashboard) {
      navigate(Path.Dashboard);
      return;
    }
    // Restrict Members from accessing Messages page via URL
    if (loginUser?.userType === UserRole.Member && path === Path.Messages) {
      actions.user.logout();
      return;
    }
    // Restrict Members from accessing Groups page via URL
    if (loginUser?.userType === UserRole.Member && path === Path.Groups) {
      actions.user.logout();
      return;
    }
    // Restrict Members from accessing People page via URL
    if (loginUser?.userType === UserRole.Member && path === Path.People) {
      actions.user.logout();
      return;
    }
    // Restrict Members from accessing Add People page via URL
    if (loginUser?.userType === UserRole.Member && path === Path.AddPerson) {
      actions.user.logout();
      return;
    }
    // Restrict Members from accessing People Dashboard page via URL
    if (loginUser?.userType === UserRole.Member && path === Path.PeopleDashboard) {
      actions.user.logout();
      return;
    }
    // Restrict Members from accessing Additional page via URL
    if (loginUser?.userType === UserRole.Member && (path === Path.PeopleAddition || path === Path.AddPersonAddition || path === Path.GroupAddition || path === Path.ReportCommunicationAddition || path === Path.ReportRecentAddition || path === Path.ReportInvalidContactsAddition || path === Path.ReportOptOutAddition || path === Path.SendAddition || path === Path.SendEmergencyAddition)) {
      actions.user.logout();
      return;
    }
    // Restrict Members from accessing Send and Send/Emergency pages via URL
    if (
      loginUser?.userType === UserRole.Member &&
      (path === Path.Send || path === Path.SendEmergency)
    ) {
      actions.user.logout();
      return;
    }
    // Restrict non-Admins from accessing protected pages via URL
    const protectedAdminPages = [
      // Path.People,
      Path.ReportCommunication,
      Path.ReportRecent,
      Path.ReportInvalidContacts,
      Path.ReportOptOut,
    ];
    if (
      loginUser?.userType !== UserRole.Admin &&
      protectedAdminPages.includes(path)
    ) {
      actions.user.logout();
    }
  }, [isMemberOrStaff, loginUser?.read_only, loginUser?.userType, path]);

  useEffect(() => {
    // When the user navigates, reset which menu item is expanded
    if (
      path.startsWith(Path.Send) ||
      path.startsWith(Path.Track) ||
      path === Path.Archived ||
      path === Path.SendQuickLaunch
    )
      setExpandedItem(Path.Send);
    else if (path.startsWith(Path.People))
      setExpandedItem(Path.People);
  }, [path]);

  function toggleExpanded(e, itemLabel) {
    e.stopPropagation();
    setExpandedItem(expandedItem === itemLabel ? null : itemLabel);
  }
  function goTo(e, path, hasChildren) {
    e.stopPropagation(); // Keep parent menu item from responding to the same click

    if (isMobile && hasChildren) {
      setExpandedItem(path);
      return;
    }

    if (path === Path.Send) actions.alerts.showCreateAlert(false);
    else if (path === Path.SendEmergency) actions.alerts.showCreateAlert(true);
    else if (path === Path.Track)
      actions.track.navigateToTrack(AlertStatus.All);
    else if (path === Path.TrackSent)
      actions.track.navigateToTrack(AlertStatus.Sent);
    else if (path === Path.TrackScheduled)
      actions.track.navigateToTrack(AlertStatus.Scheduled);
    else if (path === Path.TrackDrafts)
      actions.track.navigateToTrack(AlertStatus.Draft);
    else navigate(path);

    if (isMobile) actions.page.toggleSideBar(false);
    return false;
  }

  return (
    <>
      {isMobile && (
        <Blackout
          show={sidebarExpanded}
          onClick={() => actions.page.toggleSideBar(false)}
        />
      )}

      <div className={"side-bar" + (sidebarExpanded ? "" : " collapsed")}>
        {loginUser?.userType && (
          <>
            <SideBarItem
              text="My Dashboard"
              icon="fa fa-address-card"
              onClick={(e) => goTo(e, Path.Dashboard)}
              selected={path === Path.Dashboard}
              dataQa="dashboard"
            ></SideBarItem>
            {/* If userType 1/2/3 and read_only true: only dashboard and collapse */}
            {[1, 2, 3].includes(loginUser.userType) &&
            loginUser?.read_only ? null : (
              <>
                {/* If userType 1/2/3 and read_only false: dashboard, groups, messages, collapse */}
                {[1, 2, 3].includes(loginUser?.userType) &&
                !loginUser?.read_only ? (
                  <>
                    {/* Hide groups,people and messages for Members */}
                    {loginUser?.userType !== UserRole.Member && (
                      <>
                        <SideBarItem
                          text="Messages"
                          icon="fas fa-paper-plane"
                          onClick={(e) => goTo(e, Path.Messages)}
                          selected={path === Path.Messages}
                          dataQa="messages"
                        ></SideBarItem>
                        <SideBarItem
                          text="Groups"
                          icon="fas fa-users"
                          onClick={(e) => goTo(e, Path.Groups)}
                          selected={path === Path.Groups}
                          dataQa="groups"
                        ></SideBarItem>
                        <SideBarItem
                          text="People"
                          icon="fas fa-user"
                          onClick={(e) => goTo(e, Path.People)}
                          selected={path?.startsWith(Path.People)}
                          dataQa="people"
                        ></SideBarItem>
                      </>
                    )}
                  </>
                ) : (
                  // For all other users (not 1/2/3 or not read_only), show full menu
                  ![1, 2, 3].includes(loginUser.userType) && (
                    <>
                      <SideBarItem
                        text="Messages"
                        icon="fas fa-paper-plane"
                        onClick={(e) => goTo(e, Path.Messages)}
                        selected={path === Path.Messages}
                        dataQa="messages"
                      ></SideBarItem>
                      <SideBarItem
                        text="Templates"
                        icon="fas fa-file-alt"
                        onClick={(e) => goTo(e, Path.Templates)}
                        selected={path === Path.Templates}
                        dataQa="templates"
                      ></SideBarItem>
                      {/* Hide people for Members */}
                      {loginUser?.userType !== UserRole.Member && (
                        <SideBarItem
                          text="People"
                          icon="fas fa-user"
                          onClick={(e) => goTo(e, Path.People, true)}
                          expanded={sidebarExpanded && expandedItem === Path.People}
                          toggleExpanded={(e) => toggleExpanded(e, Path.People)}
                          selected={path?.startsWith(Path.People)}
                          dataQa="people"
                        >
                          {!sidebarExpanded && <SideBarItem text="People" />}
                          <SideBarItem
                            text="Active"
                            onClick={(e) => goTo(e, Path.People)}
                            selected={path === Path.People || (path.startsWith(Path.PeopleAddition) && !path.startsWith(Path.ArchivedPeople))}
                            dataQa="people-active"
                          />
                          <SideBarItem
                            text="Archived"
                            onClick={(e) => goTo(e, Path.ArchivedPeople)}
                            selected={path.startsWith(Path.ArchivedPeople)}
                            dataQa="people-archived"
                          />
                        </SideBarItem>
                      )}
                      {/* Hide groups for Members */}
                      {loginUser?.userType !== UserRole.Member && (
                        <SideBarItem
                          text="Groups"
                          icon="fas fa-users"
                          onClick={(e) => goTo(e, Path.Groups)}
                          selected={path === Path.Groups}
                          dataQa="groups"
                        ></SideBarItem>
                      )}
                      <SideBarItem
                        text="Admin Reports"
                        icon="fas fa-file-chart-line"
                        onClick={(e) => goTo(e, Path.ReportCommunication)}
                        expanded={
                          sidebarExpanded &&
                          expandedItem === Path.ReportCommunication
                        }
                        toggleExpanded={(e) =>
                          toggleExpanded(e, Path.ReportCommunication)
                        }
                        dataQa="reports"
                        selected={
                          path === Path.ReportCommunication ||
                          path === Path.ReportRecent ||
                          path === Path.ReportInvalidContacts ||
                          path === Path.ReportOptOut
                        }
                      >
                        {!sidebarExpanded && (
                          <SideBarItem text="Admin Reports" />
                        )}
                        <SideBarItem
                          text="Communication Report"
                          onClick={(e) => goTo(e, Path.ReportCommunication)}
                          selected={path === Path.ReportCommunication}
                          dataQa="reports-communication"
                        />
                        <SideBarItem
                          text="Recent Activity"
                          onClick={(e) => goTo(e, Path.ReportRecent)}
                          selected={path === Path.ReportRecent}
                          dataQa="reports-recent"
                        />
                        <SideBarItem
                          text="Invalid Contacts"
                          onClick={(e) => goTo(e, Path.ReportInvalidContacts)}
                          selected={path === Path.ReportInvalidContacts}
                          dataQa="reports-invalid-contacts"
                        />
                        <SideBarItem
                          text="Opt Out Report"
                          onClick={(e) => goTo(e, Path.ReportOptOut)}
                          selected={path === Path.ReportOptOut}
                          dataQa="reports-optout"
                        />
                      </SideBarItem>
                    </>
                  )
                )}
              </>
            )}
            <SideBarItem
              className="collapse-button"
              icon="far fa-chevron-circle-left"
              text="Collapse"
              onClick={() => actions.page.toggleSideBar(!sidebarExpanded)}
            />
          </>
        )}
      </div>
    </>
  );
}

function SideBarItem({
  icon,
  text,
  className,
  expanded,
  toggleExpanded,
  selected,
  children,
  onClick,
  dataQa,
}) {
  return (
    <div className={"side-bar-item-container " + (className || "")}>
      <div
        className={"side-bar-item" + (selected ? " selected" : "")}
        onClick={onClick}
        data-qa={dataQa}
      >
        <i className={"icon " + (icon || "")}></i>
        <span>{text}</span>
        {children && (
          <i
            className={"fal fa-chevron-down" + (expanded ? " expanded" : "")}
            onClick={toggleExpanded}
          />
        )}
      </div>
      {children && (
        <Resizer className="side-bar-sub-items" expanded={expanded}>
          {children}
        </Resizer>
      )}
    </div>
  );
}
